// src/components/layout/Sidebar.jsx - Updated with Admin Portal support
import React, { useState, useEffect, Fragment } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Transition, Dialog } from '@headlessui/react';
import {
    HomeIcon,
    DocumentTextIcon,
    Cog6ToothIcon,
    CreditCardIcon,
    SparklesIcon,
    XMarkIcon,
    ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';

function Sidebar({ mobileMenuOpen, onCloseMobileMenu }) {
    const location = useLocation();
    const { user } = useAuth();
    const [billingEnabled, setBillingEnabled] = useState(false);

    // console.log(user)

    // Check if billing is enabled
    useEffect(() => {
        fetch('/api/health')
            .then(res => res.json())
            .then(data => {
                setBillingEnabled(data.features?.billing || false);
            })
            .catch(() => {
                setBillingEnabled(false);
            });
    }, []);

    // Close mobile menu when route changes
    useEffect(() => {
        if (mobileMenuOpen) {
            onCloseMobileMenu();
        }
    }, [location.pathname]);

    const baseNavigation = [
        { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
        { name: 'Posts', href: '/posts', icon: DocumentTextIcon },
        { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
    ];

    // Add billing if enabled
    let navigation = billingEnabled
        ? [...baseNavigation, { name: 'Billing', href: '/billing', icon: CreditCardIcon }]
        : baseNavigation;

    // Add admin portal if user is admin
    if (user?.role === 'admin') {
        navigation = [...navigation, {
            name: 'Admin Portal',
            href: '/admin',
            icon: ShieldCheckIcon,
            isAdmin: true
        }];
    }

    const handleNavItemClick = () => {
        onCloseMobileMenu();
    };

    return (
        <>
            {/* Desktop sidebar */}
            <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-64 lg:flex-col">
                <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white border-r border-gray-200 px-6">
                    {/* Logo/Brand */}
                    <div className="flex h-16 shrink-0 items-center border-b border-gray-200">
                        <img src="/images/Bob_only.svg" alt="PostMgr" className="h-8 w-8" />
                        <span className="ml-2 text-xl font-bold text-gray-900">PostMgr</span>
                    </div>

                    {/* Navigation */}
                    <nav className="flex flex-1 flex-col">
                        <ul role="list" className="flex flex-1 flex-col gap-y-7">
                            <li>
                                <ul role="list" className="-mx-2 space-y-1">
                                    {navigation.map((item) => {
                                        const isActive = location.pathname === item.href;
                                        const isAdminItem = item.isAdmin;

                                        return (
                                            <li key={item.name}>
                                                <Link
                                                    to={item.href}
                                                    className={`${
                                                        isActive
                                                            ? 'bg-blue-50 text-blue-600 border-r-2 border-blue-600'
                                                            : isAdminItem
                                                                ? 'text-red-700 hover:text-red-600 hover:bg-red-50'
                                                                : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                                                    } group flex gap-x-3 rounded-l-md p-3 text-sm leading-6 font-semibold transition-colors`}
                                                >
                                                    <item.icon
                                                        className={`${
                                                            isActive
                                                                ? 'text-blue-600'
                                                                : isAdminItem
                                                                    ? 'text-red-600'
                                                                    : 'text-gray-400 group-hover:text-blue-600'
                                                        } h-6 w-6 shrink-0`}
                                                        aria-hidden="true"
                                                    />
                                                    {item.name}
                                                    {isAdminItem && (
                                                        <span className="ml-auto text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                                                            ADMIN
                                                        </span>
                                                    )}
                                                </Link>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>

            {/* Mobile sidebar */}
            <Transition.Root show={mobileMenuOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50 lg:hidden" onClose={onCloseMobileMenu}>
                    <Transition.Child
                        as={Fragment}
                        enter="transition-opacity ease-linear duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity ease-linear duration-300"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-900/80" />
                    </Transition.Child>

                    <div className="fixed inset-0 flex">
                        <Transition.Child
                            as={Fragment}
                            enter="transition ease-in-out duration-300 transform"
                            enterFrom="-translate-x-full"
                            enterTo="translate-x-0"
                            leave="transition ease-in-out duration-300 transform"
                            leaveFrom="translate-x-0"
                            leaveTo="-translate-x-full"
                        >
                            <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                                <Transition.Child
                                    as={Fragment}
                                    enter="ease-in-out duration-300"
                                    enterFrom="opacity-0"
                                    enterTo="opacity-100"
                                    leave="ease-in-out duration-300"
                                    leaveFrom="opacity-100"
                                    leaveTo="opacity-0"
                                >
                                    <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                                        <button type="button" className="-m-2.5 p-2.5" onClick={onCloseMobileMenu}>
                                            <span className="sr-only">Close sidebar</span>
                                            <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                                        </button>
                                    </div>
                                </Transition.Child>

                                <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-2">
                                    {/* Mobile header */}
                                    <div className="flex h-16 shrink-0 items-center">
                                        <img src="/images/Bob_only.svg" alt="PostMgr" className="h-8 w-8" />
                                        <span className="ml-2 text-xl font-bold text-gray-900">PostMgr</span>
                                    </div>

                                    {/* Mobile Navigation */}
                                    <nav className="flex flex-1 flex-col">
                                        <ul role="list" className="flex flex-1 flex-col gap-y-7">
                                            <li>
                                                <ul role="list" className="-mx-2 space-y-1">
                                                    {navigation.map((item) => {
                                                        const isActive = location.pathname === item.href;
                                                        const isAdminItem = item.isAdmin;

                                                        return (
                                                            <li key={item.name}>
                                                                <Link
                                                                    to={item.href}
                                                                    onClick={handleNavItemClick}
                                                                    className={`${
                                                                        isActive
                                                                            ? 'bg-blue-50 text-blue-600'
                                                                            : isAdminItem
                                                                                ? 'text-red-700 hover:text-red-600 hover:bg-red-50'
                                                                                : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                                                                    } group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold`}
                                                                >
                                                                    <item.icon
                                                                        className={`${
                                                                            isActive
                                                                                ? 'text-blue-600'
                                                                                : isAdminItem
                                                                                    ? 'text-red-600'
                                                                                    : 'text-gray-400 group-hover:text-blue-600'
                                                                        } h-6 w-6 shrink-0`}
                                                                        aria-hidden="true"
                                                                    />
                                                                    {item.name}
                                                                    {isAdminItem && (
                                                                        <span className="ml-auto text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                                                                            ADMIN
                                                                        </span>
                                                                    )}
                                                                </Link>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </li>
                                        </ul>
                                    </nav>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </Dialog>
            </Transition.Root>
        </>
    );
}

export default Sidebar;